import { createSelector } from 'reselect';

// Types
import { RootState } from 'types/RootState.types';

// Config
import featureConstants from '@constants/featureConstants';
import { isFeatureActive } from '@redux/reducers/config/configReducer';
import { getOrderFormOutOfStockItemsSelector } from '@redux/reducers/order-form/orderFormSelector';
import {
  getApplePayCompatibilityChecksComplete,
  getCanMakeGooglePayPaymentsSelector,
  getPaymentGetPaymentPageCallActiveSelector,
  getGooglePayPaymentsClientHasBeenCreatedSelector,
  getInitPaymentPageCallActiveSelector,
  getPaymentCardDeleteApiCallFailedSelector,
  getPaymentCardDeletedSelector,
  getPaymentCreditTypeGroupsSelector,
  getPaymentShowPartnerCardErrorSelector,
  getPaymentSubmitOrderErrorSelector,
  getPaymentWalletSelector,
  getSelectedPaymentTypeSelector,
  getShowMarketingPreferencesPromptSelector,
} from '@redux/reducers/payment/paymentSelector';
import { getPartnerDiscountEnabledSelector, getSavedPaymentCardsSelector } from '@redux/reducers/user/userSelector';
import { isBasketClearpayEligible } from '@utils/payment/isBasketClearpayEligible';
import { isBasketKlarnaEligible } from '@utils/payment/isBasketKlarnaEligible';
import { isBasketPayPalPayLaterEligible } from '@utils/payment/isBasketPayPalPayLaterEligible';
import isPayPalAvailable from '@utils/payment/isPayPalAvailable';

const getShowPaymentMessageFeatureActiveSelector = (state: RootState) =>
  isFeatureActive(state, featureConstants.PAYMENT_PAGE_BANNER_ENABLED);
const getShowCallCentreNumberOn3DSErrorSelector = (state: RootState) =>
  isFeatureActive(state, featureConstants.SHOW_CALL_CENTRE_NUMBER_ON_3DS_ERROR);

const getShouldShowClearpaySelector = (state: RootState) => isBasketClearpayEligible(state);
const getShouldShowKlarnaSelector = (state: RootState) => isBasketKlarnaEligible(state);
const getIsBasketPayPalPayLaterEligibleSelector = (state: RootState) => isBasketPayPalPayLaterEligible(state);
const getIsPayPalAvailableSelector = (state: RootState) => isPayPalAvailable(state);

const streamlinedPaymentsState = createSelector(
  [
    getApplePayCompatibilityChecksComplete,
    getPartnerDiscountEnabledSelector,
    getSavedPaymentCardsSelector,
    getSelectedPaymentTypeSelector,
    getShowPaymentMessageFeatureActiveSelector,
    getPaymentCardDeleteApiCallFailedSelector,
    getPaymentCardDeletedSelector,
    getPaymentWalletSelector,
    getShowMarketingPreferencesPromptSelector,
    getCanMakeGooglePayPaymentsSelector,
    getGooglePayPaymentsClientHasBeenCreatedSelector,
    getPaymentCreditTypeGroupsSelector,
    getShouldShowClearpaySelector,
    getShouldShowKlarnaSelector,
    getIsBasketPayPalPayLaterEligibleSelector,
    getIsPayPalAvailableSelector,
    getPaymentSubmitOrderErrorSelector,
    getOrderFormOutOfStockItemsSelector,
    getInitPaymentPageCallActiveSelector,
    getPaymentGetPaymentPageCallActiveSelector,
    getPaymentShowPartnerCardErrorSelector,
    getShowCallCentreNumberOn3DSErrorSelector,
  ],
  (
    isApplePayAvailable,
    partnerDiscountEnabled,
    savedPaymentCards,
    selectedPaymentType,
    showPaymentMessage,
    paymentCardDeleteApiCallFailed,
    paymentCardDeleted,
    paymentWallet,
    showMarketingPreferencesPrompt,
    canMakeGooglePayPayments,
    googlePayPaymentsClientHasBeenCreated,
    paymentCreditTypeGroups,
    showClearpay,
    showKlarna,
    payPalPayLaterEligible,
    showPayPal,
    submitOrderError,
    outOfStockItems,
    initPaymentPageCallActive,
    getPaymentPageCallActive,
    showPartnerCardError,
    showCallCentreNumberOn3DSError,
  ) => {
    const creditTypeGroups = paymentCreditTypeGroups || [];

    return {
      creditTypeGroups,
      isApplePayAvailable,
      isGooglePayAvailable: canMakeGooglePayPayments && googlePayPaymentsClientHasBeenCreated,
      isPayPalAvailable: showPayPal,
      outOfStockItems,
      partnerDiscountEnabled,
      paymentCardDeleteApiCallFailed,
      paymentCardDeleted,
      paymentPageAPICallActive: initPaymentPageCallActive || getPaymentPageCallActive,
      paymentWallet,
      payPalPayLaterEligible,
      savedPaymentCards,
      selectedPaymentType,
      showCallCentreNumberOn3DSError,
      showClearpay,
      showKlarna,
      showMarketingPreferencesPrompt,
      showPartnerCardError,
      showPaymentMessage,
      submitOrderError,
    };
  },
);

export default streamlinedPaymentsState;
